import type { OverviewArtifact, ResultsArtifact, ReviewBundle, ReviewIndex, ReviewRun } from './types';

const DATA_ROOT = '/data';

export async function loadReviewBundle(): Promise<ReviewBundle> {
  const index = await fetchJson<ReviewIndex>(`${DATA_ROOT}/review-index.json`);
  if (index.kind !== 'optagent_review_bundle' || index.review_schema_version !== 2) {
    throw new Error('review-index.json 不是受支持的 OptAgent review bundle v2');
  }
  const [overview, results, runs] = await Promise.all([
    fetchVerifiedJson<OverviewArtifact>(index, 'overview.json'),
    fetchVerifiedJson<ResultsArtifact>(index, 'results.json'),
    fetchVerifiedJsonLines<ReviewRun>(index, 'runs.jsonl'),
  ]);
  if (overview.render_mode !== index.render_mode) throw new Error('review bundle 的渲染模式不一致');
  const supportedReviewMode = overview.review_mode === 'single_family_focus'
    ? overview.target_families.length === 1
    : overview.review_mode === 'multi_family_suite' && overview.target_families.length > 0;
  if (!supportedReviewMode) {
    throw new Error('review bundle 必须声明单 family 或多 family 评审');
  }
  if ((index.render_mode === 'comparison') !== Boolean(overview.baseline)) {
    throw new Error('review bundle 的 Baseline 声明与渲染模式不一致');
  }
  return { index, overview, results, runs };
}

async function fetchVerifiedJson<T>(index: ReviewIndex, name: string): Promise<T> {
  return JSON.parse(await fetchVerifiedText(index, name)) as T;
}

async function fetchVerifiedJsonLines<T>(index: ReviewIndex, name: string): Promise<T[]> {
  return (await fetchVerifiedText(index, name))
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line) => JSON.parse(line) as T);
}

async function fetchVerifiedText(index: ReviewIndex, name: string): Promise<string> {
  const entry = index.artifacts[name];
  if (!entry) throw new Error(`review bundle 未声明 ${name}`);
  if (entry.path !== name) throw new Error(`review bundle 中 ${name} 的路径无效`);
  const response = await fetch(`${DATA_ROOT}/${entry.path}`);
  if (!response.ok) throw new Error(`加载 ${name} 失败：HTTP ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength !== entry.bytes) throw new Error(`${name} 字节数不匹配`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const actual = [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
  if (actual !== entry.sha256.replace(/^sha256:/, '')) throw new Error(`${name} 校验和不匹配`);
  return new TextDecoder().decode(bytes);
}

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) throw new Error(`加载 ${path} 失败：HTTP ${response.status}`);
  return (await response.json()) as T;
}
